import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Search, Phone, Calendar, Check, Clock, X, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallLogs = () => {
  const [logs, setLogs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [callers, setCallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    customer_id: "",
    caller_id: "",
    search: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, customersRes, callersRes] = await Promise.all([
        axios.get(`${API}/call-logs`),
        axios.get(`${API}/users`, { params: { role: "customer" } }),
        axios.get(`${API}/users`, { params: { role: "caller" } }),
      ]);
      setLogs(logsRes.data);
      setCustomers(customersRes.data);
      setCallers(callersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load call logs");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (name, value) => {
    const newFilters = { ...filters, [name]: value === "all" ? "" : value };
    setFilters(newFilters);

    try {
      const params = {};
      if (newFilters.customer_id) params.customer_id = newFilters.customer_id;
      if (newFilters.caller_id) params.caller_id = newFilters.caller_id;
      
      const response = await axios.get(`${API}/call-logs`, { params });
      setLogs(response.data);
    } catch (error) {
      toast.error("Failed to filter logs");
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm("Are you sure you want to delete this call log?")) return;

    try {
      await axios.delete(`${API}/call-logs/${logId}`);
      toast.success("Call log deleted");
      setLogs(logs.filter((log) => log.id !== logId));
    } catch (error) {
      toast.error("Failed to delete call log");
    }
  };

  const getQualifiedBadge = (status) => {
    if (status === "Y") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm bg-green-600/20 text-green-400">
          <Check size={12} />
          Qualified
        </span>
      );
    } else if (status === "P") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm bg-yellow-600/20 text-yellow-400">
          <Clock size={12} />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm bg-[#1a3a6a] text-white/50">
        <X size={12} />
        Not Qualified
      </span>
    );
  };

  const filteredLogs = logs.filter((log) => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      log.customer_name?.toLowerCase().includes(search) ||
      log.caller_name?.toLowerCase().includes(search) ||
      log.phone_number?.includes(search) ||
      log.property_address?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-full bg-[#f4cc1e] p-4 sm:p-6 rounded-xl min-h-screen" data-testid="call-logs-page">
      {/* Header */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white font-['Chivo']">Call Logs</h1>
              <p className="text-sm text-white/60">View and manage all call records</p>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <Phone size={16} className="text-[#f4cc1e]" />
              <span>{filteredLogs.length} records</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                placeholder="Search logs..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 bg-[#0d2a5a] border-[#1a3a6a] text-white"
                data-testid="search-input"
              />
            </div>
            <Select
              value={filters.customer_id || "all"}
              onValueChange={(value) => handleFilterChange("customer_id", value)}
            >
              <SelectTrigger className="w-full sm:w-48 bg-[#0d2a5a] border-[#1a3a6a] text-white" data-testid="customer-filter">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                <SelectItem value="all" className="text-white">All Customers</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="text-white">
                    {c.company_name || c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.caller_id || "all"}
              onValueChange={(value) => handleFilterChange("caller_id", value)}
            >
              <SelectTrigger className="w-full sm:w-48 bg-[#0d2a5a] border-[#1a3a6a] text-white" data-testid="caller-filter">
                <SelectValue placeholder="All Callers" />
              </SelectTrigger>
              <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                <SelectItem value="all" className="text-white">All Callers</SelectItem>
                {callers.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="text-white">
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#1a3a6a]">
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold">
                    Date
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold">
                    Customer
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold">
                    Caller
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold">
                    Contact
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold">
                    Campaign
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold">
                    Status
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold">
                    Result
                  </TableHead>
                  <TableHead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs font-bold text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-white/50">
                      No call logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-[#0d2a5a] border-b border-[#1a3a6a]">
                      <TableCell className="font-mono text-sm text-white">
                        {log.date_of_call}
                      </TableCell>
                      <TableCell className="font-medium text-white">
                        {log.customer_name || "N/A"}
                      </TableCell>
                      <TableCell className="text-white/70">{log.caller_name || "N/A"}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {log.ho_manager_name && (
                            <p className="text-sm text-white">{log.ho_manager_name}</p>
                          )}
                          {log.phone_number && (
                            <p className="text-xs text-white/50 font-mono">
                              {log.phone_number}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-white/70">
                        {log.campaign || "-"}
                      </TableCell>
                      <TableCell>
                        {getQualifiedBadge(log.qualified_payable)}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm capitalize text-white/70">
                          {log.call_result?.replace(/_/g, " ") || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(log.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          data-testid={`delete-log-${log.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CallLogs;
